import _React, { useMemo, useEffect, useCallback, useRef } from 'react';
import { Form, FormInstance } from '@oscar/theme';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from '@oscar/utils';
import { validateRequest } from '@oscar/request';
import { FormOptions, ConcreteForm } from '../typed';
import {
  fetchData,
  initializeForm,
  preloadData,
  release,
  reset,
} from '../reducer';
import { selectorFactory } from '../selectors';
import { correctFormName } from '../utils';
import { isEmpty } from 'lodash';

export default (config: FormOptions, form?: FormInstance<any>, data?: any) => {
  const options = useMemo(
    () => ({
      ...config,
      name: correctFormName(config.name),
    }),
    [config.name]
  );

  const selectors = useMemo(
    () => selectorFactory(options.name, options?.reducerName),
    [options.name, options?.reducerName]
  );

  const formRef = useRef<ConcreteForm>();

  const initialValues = useSelector(selectors.getInitialValues);
  const initialTime: number = useSelector(selectors.getInitalTime);

  const isLoading = useSelector((state) =>
    selectors.getLoadingByKey(state, `${options?.request?.name}`)
  );

  const dispatch = useDispatch();
  const [hookForm] = Form.useForm(form);

  const handleReset = useCallback(
    (value?: any, lock?: number) => {
      if (initialTime) {
        hookForm.resetFields(
          !isEmpty(initialValues) ? initialValues : undefined
        );
        dispatch(
          reset({
            metadata: {
              form: options.name,
            },
            data: { initialValues: value ? value : initialValues, lock },
          })
        );
      }
    },
    [initialTime, initialValues]
  );

  const wrapForm = useMemo(() => {
    formRef.current = {
      ...hookForm,
      loading: isLoading,
      reset: handleReset,
    };

    return formRef.current;
  }, [initialTime]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.loading = isLoading;
    }
  }, [isLoading]);

  useEffect(() => {
    dispatch(
      initializeForm({
        metadata: {
          form: options.name,
        },
      })
    );
    return () => {
      if (!options.keepDirty) {
        hookForm.resetFields();
        dispatch(
          release({
            metadata: {
              form: options.name,
            },
          })
        );
      }
    };
  }, []);

  useDeepCompareEffect(() => {
    hookForm.setFieldsValue(initialValues);
  }, [initialValues]);

  useDeepCompareEffect(() => {
    if (initialTime && options.request) {
      const request = validateRequest(options?.request, data);
      if (request.shouldRequest) {
        dispatch(
          fetchData.action({
            metadata: {
              form: options.name,
            },
            request,
          })
        );
      }
    }
  }, [initialTime]);

  useDeepCompareEffect(() => {
    if (initialTime && options.preload) {
      const requests = options.preload
        .map((request) => validateRequest(request, data))
        .filter((request) => request.shouldRequest === true)
        .map((request) => ({
          metadata: {
            form: options.name,
          },
          request: request,
        }));
      if (requests.length > 0) {
        dispatch(preloadData.action(requests));
      }
    }
  }, [initialTime]);

  return [wrapForm];
};
